import { useCallback, useEffect, useState } from 'react';
import {
  matterApi, memberApi, attorneyApi, attorneyPortalApi,
  type MatterAccessGrant, type MatterAccessLevel, type Member, type MatterDetail,
  type Attorney, type AttorneyPortalGrant,
} from '../../api';
import Select from '../common/Select';
import { humaniseRole, humanise } from '../../lib/labels';

type Props = { matterId: number | string; matter: MatterDetail; onChange: () => void };

const CONFIDENTIALITY_MODES: Array<{ value: 'standard' | 'restricted' | 'walled'; label: string; description: string }> = [
  { value: 'standard',   label: 'Standard',   description: 'Visible to every member with matter.list capability.' },
  { value: 'restricted', label: 'Restricted', description: 'Listed for all members, but only users with an explicit access grant can open it.' },
  { value: 'walled',     label: 'Walled',     description: 'Hidden entirely from members without an explicit grant. Ethical wall for conflicts.' },
];

const ACCESS_LEVELS: Array<{ value: MatterAccessLevel; label: string; hint: string }> = [
  { value: 'none',    label: 'None',    hint: 'No access (blocks read on walled matters)' },
  { value: 'read',    label: 'Read',    hint: 'View only' },
  { value: 'comment', label: 'Comment', hint: 'Read + add notes/events' },
  { value: 'write',   label: 'Write',   hint: 'Read + edit (no ACL changes)' },
  { value: 'manage',  label: 'Manage',  hint: 'Full control including ACL and confidentiality' },
];

export default function AccessPanel({ matterId, matter, onChange }: Props) {
  const [grants, setGrants] = useState<MatterAccessGrant[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingMode, setSavingMode] = useState(false);
  const [savingAcl, setSavingAcl] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [addUserId, setAddUserId] = useState<number | null>(null);
  const [addLevel, setAddLevel] = useState<MatterAccessLevel>('read');
  const [reason, setReason] = useState(matter.confidentiality?.reason || '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        matterApi.getAccess(matterId),
        memberApi.list(),
      ]);
      setGrants(a.access || []);
      setMembers(m.members || []);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const memberById = new Map(members.map((m) => [String(m.user_id), m]));

  const setMode = async (mode: 'standard' | 'restricted' | 'walled') => {
    setSavingMode(true);
    setError(null);
    try {
      await matterApi.setConfidentiality(matterId, mode, reason || undefined);
      onChange();
    } catch (e) { setError((e as Error).message); }
    finally { setSavingMode(false); }
  };

  const addGrant = async () => {
    if (!addUserId) { setError('Pick a user.'); return; }
    setSavingAcl(true);
    setError(null);
    try {
      await matterApi.setAccess(matterId, [{ user_id: addUserId, access_level: addLevel }]);
      setAddUserId(null);
      setAddLevel('read');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setSavingAcl(false); }
  };

  const currentMode = matter.confidentiality?.mode || 'standard';
  const needsGrants = currentMode === 'walled' || currentMode === 'restricted';

  return (
    <div className="stack">
      <h3 style={{ margin: 0 }}>Access control</h3>

      {/* Confidentiality mode */}
      <div className="card">
        <div className="stack">
          <div>
            <label>Confidentiality mode</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 8 }}>
              {CONFIDENTIALITY_MODES.map((m) => {
                const active = currentMode === m.value;
                return (
                  <button
                    key={m.value}
                    onClick={() => setMode(m.value)}
                    disabled={savingMode || active}
                    style={{
                      textAlign: 'left',
                      padding: 14,
                      border: `2px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
                      background: active ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                      borderRadius: 'var(--radius)',
                      cursor: active ? 'default' : 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: active ? 'var(--color-accent)' : 'var(--color-text)' }}>
                      {m.label}
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{m.description}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label>Reason (optional, but recommended for walled matters)</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => { if ((reason || '') !== (matter.confidentiality?.reason || '')) setMode(currentMode as 'standard' | 'restricted' | 'walled'); }}
              placeholder="e.g. Conflict wall — previously advised opposing party"
            />
          </div>
        </div>
      </div>

      {/* Per-user grants */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={sectionHeader}>
          <h4 style={{ margin: 0 }}>User-level access</h4>
          <span className="muted" style={{ fontSize: 12 }}>
            {needsGrants ? 'Required — this matter has an access gate' : 'Baseline access applies to all members'}
          </span>
        </div>
        <div style={{ padding: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ flex: 2, minWidth: 240 }}>
            <label>Member</label>
            <Select
              value={addUserId != null ? String(addUserId) : ''}
              onChange={(v) => setAddUserId(v ? Number(v) : null)}
              searchPlaceholder="Search members…"
              placeholder="Pick a member…"
              options={members.map((m) => ({
                value: String(m.user_id),
                label: m.display_name,
                hint: m.role,
              }))}
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label>Access level</label>
            <Select
              value={addLevel}
              onChange={(v) => setAddLevel(v as MatterAccessLevel)}
              options={ACCESS_LEVELS.map((l) => ({ value: l.value, label: l.label, hint: l.hint }))}
            />
          </div>
          <button className="primary" onClick={addGrant} disabled={savingAcl}>
            {savingAcl ? 'Saving…' : 'Grant access'}
          </button>
        </div>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : grants.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">
            {needsGrants
              ? 'No user grants yet. Only you (as super-admin / owner) can currently view this matter.'
              : 'No per-user overrides. All members have baseline access.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr><th>Member</th><th>Level</th><th>Granted</th><th style={{ width: 40 }}></th></tr>
            </thead>
            <tbody>
              {grants.map((g) => {
                const m = memberById.get(String(g.user_id));
                return (
                  <tr key={String(g.ROWID)}>
                    <td>
                      <div>{m?.display_name || `User #${g.user_id}`}</div>
                      {m && <div className="muted" style={{ fontSize: 12 }}>{m.email} · {humaniseRole(m.role)}</div>}
                    </td>
                    <td><span className="pill">{humanise(g.access_level)}</span></td>
                    <td className="muted" style={{ fontSize: 12 }}>{g.granted_at?.slice(0, 10)}</td>
                    <td></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ExternalAttorneyPortalSection matterId={matterId} />

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}>{error}</div>}
    </div>
  );
}

// ---------- External attorney portal access ----------
// The instructing attorney gets a tokenised URL that shows a read-only
// snapshot of this matter — no login needed, revokable any time. The raw
// token is only returned on creation, so the "copy link" modal pops once.

function ExternalAttorneyPortalSection({ matterId }: { matterId: number | string }) {
  const [grants, setGrants] = useState<AttorneyPortalGrant[]>([]);
  const [attorneys, setAttorneys] = useState<Attorney[]>([]);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(false);
  const [picked, setPicked] = useState<string>('');
  const [ttlDays, setTtlDays] = useState('90');
  const [error, setError] = useState<string | null>(null);
  const [justGranted, setJustGranted] = useState<{ url: string; attorney: string; expires_at: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [g, a] = await Promise.all([
        attorneyPortalApi.list(matterId),
        attorneyApi.list({}),
      ]);
      setGrants(g.access || []);
      setAttorneys(a.attorneys || []);
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    if (!picked) { setError('Pick an attorney.'); return; }
    setGranting(true); setError(null);
    try {
      const r = await attorneyPortalApi.grant(matterId, {
        attorney_id: Number(picked),
        ttl_days: Number(ttlDays) || 90,
      });
      setJustGranted({ url: r.url, attorney: r.attorney.name, expires_at: r.expires_at });
      try { await navigator.clipboard.writeText(r.url); } catch { /* clipboard denied; URL still shown */ }
      setPicked('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally { setGranting(false); }
  };

  const revoke = async (g: AttorneyPortalGrant) => {
    if (!confirm(`Revoke portal access for ${g.attorney?.email || `attorney #${g.attorney_id}`}? The link will stop working immediately.`)) return;
    try {
      await attorneyPortalApi.revoke(matterId, g.ROWID);
      await load();
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="card stack">
      <div>
        <h4 style={{ margin: 0 }}>External attorney portal</h4>
        <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
          Generate a tokenised URL the instructing attorney can use to see a read-only snapshot of this matter — overview, parties, deadlines, events, and any invoices addressed to them. No login, revokable any time.
        </p>
      </div>

      <div className="row">
        <div className="grow">
          <Select
            value={picked}
            onChange={setPicked}
            searchPlaceholder="Search attorneys…"
            options={attorneys.map((a) => ({
              value: String(a.ROWID),
              label: `${[a.first_name, a.last_name].filter(Boolean).join(' ')}${a.email ? ` · ${a.email}` : ''}`,
            }))}
          />
        </div>
        <div style={{ width: 110 }}>
          <input
            type="number"
            min="1"
            value={ttlDays}
            onChange={(e) => setTtlDays(e.target.value)}
            placeholder="TTL days"
            title="Days until the link expires"
          />
        </div>
        <button className="primary" onClick={grant} disabled={granting || !picked}>
          {granting ? '…' : 'Grant access'}
        </button>
      </div>

      {loading ? (
        <div className="muted" style={{ padding: 8 }}>Loading…</div>
      ) : grants.length === 0 ? (
        <div className="muted" style={{ padding: 8, fontSize: 13 }}>No external access granted on this matter.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Attorney</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {grants.map((g) => (
              <tr key={String(g.ROWID)}>
                <td>
                  <div>{[g.attorney?.first_name, g.attorney?.last_name].filter(Boolean).join(' ') || `Attorney #${g.attorney_id}`}</div>
                  {g.attorney?.email && <div className="muted" style={{ fontSize: 12 }}>{g.attorney.email}</div>}
                </td>
                <td><span className={`pill ${g.status === 'active' ? 'success' : ''}`}>{g.status}</span></td>
                <td className="muted" style={{ fontSize: 12 }}>{g.expires_at?.slice(0, 10) || '—'}</td>
                <td className="muted" style={{ fontSize: 12 }}>{g.last_used_at?.slice(0, 10) || 'never'}</td>
                <td style={{ textAlign: 'right' }}>
                  {g.status === 'active' && (
                    <button className="ghost" style={{ color: 'var(--color-danger)' }} onClick={() => revoke(g)}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      {justGranted && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={() => setJustGranted(null)}
        >
          <div className="card stack" style={{ width: 620 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Portal link for {justGranted.attorney}</h3>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Copied to clipboard. This is the only time we'll show it — store or send it now. Expires {justGranted.expires_at?.slice(0, 10)}.
            </p>
            <input value={justGranted.url} readOnly style={{ fontSize: 12 }} onFocus={(e) => e.currentTarget.select()} />
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setJustGranted(null)}>Close</button>
              <a href={justGranted.url} target="_blank" rel="noopener noreferrer">
                <button className="primary">Open</button>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const sectionHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)',
};
